/**
 * Globale Suche (Roadmap F-18) - rein und getestet.
 *
 * `suchIndex` macht aus allen Inhaltsarten gleichfoermige Eintraege, `suchen`
 * findet darin: Gross-/Kleinschreibung egal, Wortteile zaehlen ("Klaus"
 * findet "Klausur"), mehrere Woerter muessen alle vorkommen, `#wort` sucht
 * nach Schlagwoertern. `markiere` zerlegt einen Text fuers Hervorheben.
 *
 * Bei der Datenmenge eines Menschen ist ein Durchlauf ueber alles je
 * Tastendruck schneller als jede Indexpflege - und nie veraltet.
 */
import { herkunft } from '@/domain/journal/thoughts';
import { MONATS_FRAGEN, reflexionsTexte, WOCHEN_FRAGEN } from '@/domain/journal/reflection';

/** Uhrzeiten und Checklisten eigener Felder sind Text (F-32) - Zahlen und Ja/Nein nicht. */
const textWerte = (w?: Record<string, unknown>) => Object.values(w ?? {}).flatMap((v) => (typeof v === 'string' ? [v] : Array.isArray(v) ? [v.join(', ')] : []));
import { normalizeTag } from '@/domain/resources/search';
import { monatName } from '@/domain/review/periods';
import { typInfo } from '@/domain/wissen';
import type { FocusGoal } from '@/domain/planning/focus';
import type { Liste, ListenEintrag } from '@/domain/listen/typen';
import type { PlanEinheit, TrainingsPlan, TrainingsProtokoll, Uebung } from '@/domain/training/typen';
import type { Lebensmittel, Rezept } from '@/domain/ernaehrung/typen';
import type {
  Book, CalendarEvent, Goal, ID, JournalEntry, LocalDate, MonthlyReview, Project, Resource, Task, Thought, WeeklyReview,
} from '@/domain/types';
import { formatDate, formatDe } from '@/lib/datetime';

export type SuchArt = 'notiz' | 'gedanke' | 'aufgabe' | 'liste' | 'training' | 'ernaehrung' | 'projekt' | 'ziel' | 'journal' | 'reflexion' | 'planung' | 'wissen' | 'termin';

export const SUCH_ARTEN: { key: SuchArt; label: string }[] = [
  { key: 'notiz', label: 'Notizen' },
  { key: 'gedanke', label: 'Gedanken' },
  { key: 'aufgabe', label: 'Aufgaben' },
  { key: 'liste', label: 'Listen' },
  { key: 'training', label: 'Training' },
  { key: 'ernaehrung', label: 'Ernährung' },
  { key: 'projekt', label: 'Projekte' },
  { key: 'ziel', label: 'Ziele' },
  { key: 'journal', label: 'Journal' },
  { key: 'reflexion', label: 'Reflexionen' },
  { key: 'planung', label: 'Planung' },
  { key: 'wissen', label: 'Wissen' },
  { key: 'termin', label: 'Termine' },
];

/** Wohin ein Treffer fuehrt: eine Seite, eine Aufgabe im Dialog oder ein Termin im Kalender. */
export type SuchZiel =
  | { pfad: string }
  | { aufgabe: ID }
  | { termin: ID; datum: LocalDate };

export interface SuchEintrag {
  art: SuchArt;
  /** Eindeutig ueber alle Arten, z. B. `aufgabe:<id>`. */
  key: string;
  titel: string;
  /** Was es ist, z. B. "Aufgabe" oder "Journal vom 12.09.2026". */
  was: string;
  texte: string[];
  tags: string[];
  /** Erledigt, abgeschlossen, archiviert oder vorbei - ausblendbar. */
  erledigt: boolean;
  ziel: SuchZiel;
}

export interface SuchDaten {
  resources: Resource[];
  thoughts: Thought[];
  tasks: Task[];
  projects: Project[];
  goals: Goal[];
  journal: JournalEntry[];
  weeklyReviews: WeeklyReview[];
  monthlyReviews: MonthlyReview[];
  focusGoals: FocusGoal[];
  books: Book[];
  events: CalendarEvent[];
  /** Eigene Listen (F-33) - fehlen sie, gibt es keine Listentreffer. */
  listen?: Liste[];
  listenEintraege?: ListenEintrag[];
  /** Training (F-34): Plaene, Einheiten, Uebungen. */
  trainingsplaene?: TrainingsPlan[];
  planEinheiten?: PlanEinheit[];
  uebungen?: Uebung[];
  trainingsprotokolle?: TrainingsProtokoll[];
  /** Ernaehrung (F-35): Lebensmittel und Rezepte. */
  lebensmittel?: Lebensmittel[];
  rezepte?: Rezept[];
}

const voll = (xs: (string | undefined)[]) => xs.filter((x): x is string => Boolean(x && x.trim()));

function kw(key: string): string {
  const [jahr, woche] = key.split('-W');
  return `KW ${Number(woche)}/${jahr}`;
}

export function suchIndex(d: SuchDaten, heute: LocalDate): SuchEintrag[] {
  const liste: SuchEintrag[] = [];
  for (const r of d.resources) {
    liste.push({
      art: 'notiz', key: `notiz:${r.id}`, titel: r.title || 'Ohne Titel',
      was: r.subjectId ? 'Mitschrift' : r.kind === 'link' ? 'Lesezeichen' : 'Notiz',
      texte: voll([r.body, r.url]), tags: r.tags, erledigt: r.bucket === 'archiv',
      ziel: { pfad: `/ressourcen?notiz=${encodeURIComponent(r.id)}` },
    });
  }
  for (const g of d.thoughts) {
    const her = herkunft(g);
    liste.push({
      art: 'gedanke', key: `gedanke:${g.id}`, titel: g.text, was: `Gedanke · ${her.text}`,
      texte: voll([g.note]), tags: [], erledigt: g.status === 'erledigt',
      ziel: { pfad: g.gemerkt ? `/ressourcen?gedanke=${encodeURIComponent(g.id)}` : her.pfad },
    });
  }
  for (const t of d.tasks) {
    liste.push({
      art: 'aufgabe', key: `aufgabe:${t.id}`, titel: t.title, was: 'Aufgabe',
      texte: voll([t.notes]), tags: t.contexts ?? [], erledigt: t.status === 'erledigt', ziel: { aufgabe: t.id },
    });
  }
  const listenNamen = new Map((d.listen ?? []).map((l) => [l.id, l]));
  for (const l of d.listen ?? []) {
    liste.push({
      art: 'liste', key: `liste:${l.id}`, titel: l.name, was: 'Liste', texte: voll(l.gruppen), tags: [],
      erledigt: l.archiviert, ziel: { pfad: `/listen/${encodeURIComponent(l.id)}` },
    });
  }
  for (const e of d.listenEintraege ?? []) {
    const l = listenNamen.get(e.listeId);
    if (!l) continue;
    liste.push({
      art: 'liste', key: `listeneintrag:${e.id}`, titel: e.menge ? `${e.menge} ${e.text}` : e.text, was: `Eintrag in „${l.name}“`,
      texte: voll([e.notiz, e.link, e.gruppe]), tags: [], erledigt: e.erledigt === 1 || l.archiviert,
      ziel: { pfad: `/listen/${encodeURIComponent(l.id)}` },
    });
  }
  const planNamen = new Map((d.trainingsplaene ?? []).map((p) => [p.id, p]));
  for (const p of d.trainingsplaene ?? []) {
    liste.push({
      art: 'training', key: `trainingsplan:${p.id}`, titel: p.name, was: `Trainingsplan, Version ${p.version}`, texte: voll([p.notiz]), tags: [],
      erledigt: Boolean(p.gueltigBis), ziel: { pfad: `/training/plan?plan=${encodeURIComponent(p.id)}` },
    });
  }
  const uebungsNamen = new Map((d.uebungen ?? []).map((u) => [u.id, u.name]));
  for (const e of d.planEinheiten ?? []) {
    const p = planNamen.get(e.planId);
    liste.push({
      art: 'training', key: `einheit:${e.id}`, titel: e.name, was: `Einheit in „${p?.name ?? 'Plan'}“`,
      texte: voll([e.aufwaermen, ...e.uebungen.map((u) => uebungsNamen.get(u.uebungId)), ...e.uebungen.map((u) => u.notiz)]), tags: [],
      erledigt: Boolean(p?.gueltigBis), ziel: { pfad: `/training/einheit/${encodeURIComponent(e.id)}` },
    });
  }
  for (const t of d.trainingsprotokolle ?? []) {
    liste.push({
      art: 'training', key: `protokoll:${t.id}`, titel: t.name, was: `Training vom ${formatDate(t.datum)}`,
      texte: voll([t.notiz, ...t.uebungen.map((u) => uebungsNamen.get(u.uebungId))]), tags: [],
      erledigt: t.status === 'fertig', ziel: { pfad: `/training/live/${encodeURIComponent(t.id)}` },
    });
  }
  for (const u of d.uebungen ?? []) {
    liste.push({
      art: 'training', key: `uebung:${u.id}`, titel: u.name, was: 'Übung', texte: voll([u.hinweis, ...(u.stufen ?? [])]), tags: [],
      erledigt: Boolean(u.archiviert), ziel: { pfad: `/training/uebungen?uebung=${encodeURIComponent(u.id)}` },
    });
  }
  for (const l of d.lebensmittel ?? []) {
    liste.push({
      art: 'ernaehrung', key: `lebensmittel:${l.id}`, titel: l.name, was: 'Lebensmittel', texte: voll([l.marke, l.barcode]), tags: [],
      erledigt: Boolean(l.archiviert), ziel: { pfad: `/ernaehrung/lebensmittel?id=${encodeURIComponent(l.id)}` },
    });
  }
  const lmNamen = new Map((d.lebensmittel ?? []).map((l) => [l.id, l.name]));
  for (const r of d.rezepte ?? []) {
    liste.push({
      art: 'ernaehrung', key: `rezept:${r.id}`, titel: r.name, was: 'Rezept', texte: voll([r.zubereitung, ...r.zutaten.map((z) => lmNamen.get(z.lebensmittelId))]), tags: [],
      erledigt: false, ziel: { pfad: `/ernaehrung/rezepte?id=${encodeURIComponent(r.id)}` },
    });
  }
  for (const p of d.projects) {
    liste.push({
      art: 'projekt', key: `projekt:${p.id}`, titel: p.title, was: 'Projekt',
      texte: voll([p.description, p.notes, ...(p.milestones ?? []).map((m) => m.title)]), tags: [],
      erledigt: p.status === 'abgeschlossen', ziel: { pfad: `/projekte/${encodeURIComponent(p.id)}` },
    });
  }
  for (const g of d.goals) {
    liste.push({
      art: 'ziel', key: `ziel:${g.id}`, titel: g.title, was: 'Ziel',
      texte: voll([g.why, g.notes, ...g.keyResults.map((k) => k.title)]), tags: [],
      erledigt: g.status === 'erreicht' || g.status === 'verworfen', ziel: { pfad: `/ziele/${encodeURIComponent(g.id)}` },
    });
  }
  for (const j of d.journal) {
    const r = j.reflexion;
    liste.push({
      art: 'journal', key: `journal:${j.id}`,
      titel: `${j.type === 'morgen' ? 'Morgenjournal' : j.type === 'abend' ? 'Abendjournal' : 'Journal'} vom ${formatDate(j.date)}`,
      was: `Journal vom ${formatDate(j.date)}`,
      texte: voll([j.body, ...(j.gratitude ?? []), ...reflexionsTexte(r)]),
      tags: [], erledigt: false, ziel: { pfad: `/journal?datum=${j.date}${j.type === 'frei' ? '' : `&typ=${j.type}`}` },
    });
  }
  for (const w of d.weeklyReviews) {
    liste.push({
      art: 'reflexion', key: `reflexion:w:${w.id}`, titel: `Wochenreflexion ${kw(w.isoWeek)}`, was: 'Wochenreflexion',
      texte: voll([...Object.values(w.answers ?? {}), ...textWerte(w.werte), ...eigeneFragen(w.wortlaut)]), tags: [], erledigt: false, ziel: { pfad: `/rueckblick?woche=${w.isoWeek}` },
    });
  }
  for (const m of d.monthlyReviews) {
    liste.push({
      art: 'reflexion', key: `reflexion:m:${m.id}`, titel: `Monatsreflexion ${monatName(m.month)}`, was: 'Monatsreflexion',
      texte: voll([...Object.values(m.answers ?? {}), ...textWerte(m.werte), ...eigeneFragen(m.wortlaut)]), tags: [], erledigt: false, ziel: { pfad: `/rueckblick?monat=${m.month}` },
    });
  }
  for (const f of d.focusGoals) {
    const woche = f.art === 'woche';
    liste.push({
      art: 'planung', key: `planung:${f.id}`, titel: f.title,
      was: woche ? `Wochenziel ${kw(f.key)}` : `Monatsziel ${monatName(f.key)}`, texte: [], tags: [],
      erledigt: f.status !== 'offen', ziel: { pfad: `/rueckblick?${woche ? 'wochenplanung' : 'monatsplanung'}=${f.key}` },
    });
  }
  for (const b of d.books) {
    liste.push({
      art: 'wissen', key: `wissen:${b.id}`, titel: b.title, was: typInfo(b.typ).label,
      texte: voll([b.author, b.url, b.notes, b.erkenntnisse, ...(b.stellen ?? []).map((s) => `${s.ort} ${s.text}`)]),
      tags: b.tags, erledigt: b.status === 'gelesen' || b.status === 'abgebrochen', ziel: { pfad: `/wissen/${encodeURIComponent(b.id)}` },
    });
  }
  for (const e of d.events) {
    const datum = String(e.start).slice(0, 10) as LocalDate;
    const ende = String(e.end).slice(0, 10);
    liste.push({
      art: 'termin', key: `termin:${e.id}`, titel: e.title || 'Ohne Titel',
      was: e.rrule ? `Serientermin ab ${formatDe(datum, 'EEEEEE dd.MM.yyyy')}` : `Termin am ${formatDe(datum, 'EEEEEE dd.MM.yyyy')}`,
      texte: voll([e.notes, e.location]), tags: [],
      erledigt: !e.rrule && ende < heute, ziel: { termin: e.recurringEventId ?? e.id, datum },
    });
  }
  return liste;
}

export interface Stueck { text: string; treffer: boolean }

export interface SuchTreffer {
  eintrag: SuchEintrag;
  titel: Stueck[];
  /** Ausschnitt aus dem ersten Text mit Treffer - leer, wenn nur der Titel passt. */
  ausschnitt: Stueck[];
}

interface Anfrage { woerter: string[]; tag: string | null }

function zerlege(query: string): Anfrage {
  const q = query.trim();
  if (q.startsWith('#')) return { woerter: [], tag: normalizeTag(q.slice(1)) || null };
  return { woerter: q.toLocaleLowerCase('de').split(/\s+/).filter(Boolean), tag: null };
}

const klein = (s: string) => s.toLocaleLowerCase('de');

function passt(e: SuchEintrag, a: Anfrage): boolean {
  if (a.tag) return e.tags.some((t) => normalizeTag(t).includes(a.tag!));
  if (a.woerter.length === 0) return false;
  const heuhaufen = klein([e.titel, ...e.texte, ...e.tags].join('\n'));
  return a.woerter.every((w) => heuhaufen.includes(w));
}

/** Zerlegt `text` in Stuecke mit und ohne Treffer - fuer `<mark>`. */
export function markiere(text: string, woerter: string[]): Stueck[] {
  const k = klein(text);
  const bereiche: [number, number][] = [];
  for (const w of woerter) {
    if (!w) continue;
    for (let i = k.indexOf(w); i !== -1; i = k.indexOf(w, i + w.length)) bereiche.push([i, i + w.length]);
  }
  if (bereiche.length === 0) return [{ text, treffer: false }];
  bereiche.sort((a, b) => a[0] - b[0]);
  const zusammen: [number, number][] = [];
  for (const b of bereiche) {
    const letzter = zusammen.at(-1);
    if (letzter && b[0] <= letzter[1]) letzter[1] = Math.max(letzter[1], b[1]);
    else zusammen.push([...b]);
  }
  const stuecke: Stueck[] = [];
  let pos = 0;
  for (const [von, bis] of zusammen) {
    if (von > pos) stuecke.push({ text: text.slice(pos, von), treffer: false });
    stuecke.push({ text: text.slice(von, bis), treffer: true });
    pos = bis;
  }
  if (pos < text.length) stuecke.push({ text: text.slice(pos), treffer: false });
  return stuecke;
}

/** Etwa `breite` Zeichen um den ersten Treffer, mit "…" an abgeschnittenen Enden. */
function ausschnitt(text: string, woerter: string[], breite = 90): Stueck[] {
  const flach = text.replace(/\s+/g, ' ').trim();
  const k = klein(flach);
  const erster = Math.min(...woerter.map((w) => k.indexOf(w)).filter((i) => i >= 0));
  const von = Math.max(0, erster - Math.floor(breite / 3));
  const bis = Math.min(flach.length, von + breite);
  const stueck = `${von > 0 ? '…' : ''}${flach.slice(von, bis)}${bis < flach.length ? '…' : ''}`;
  return markiere(stueck, woerter);
}

export interface SuchOptionen {
  /** Leer = alle Arten. */
  arten?: SuchArt[];
  erledigteAusblenden?: boolean;
}

export interface SuchErgebnis {
  treffer: SuchTreffer[];
  /** Treffer je Art - vor dem Art-Filter, damit die Knoepfe ihre Zahl zeigen. */
  anzahl: Record<SuchArt, number>;
  woerter: string[];
}

export function suchen(index: SuchEintrag[], query: string, opt: SuchOptionen = {}): SuchErgebnis {
  const a = zerlege(query);
  const anzahl = Object.fromEntries(SUCH_ARTEN.map((x) => [x.key, 0])) as Record<SuchArt, number>;
  const passend = index.filter((e) => passt(e, a) && !(opt.erledigteAusblenden && e.erledigt));
  for (const e of passend) anzahl[e.art] += 1;
  const arten = opt.arten?.length ? new Set(opt.arten) : null;
  const rang = (e: SuchEintrag) => (a.woerter.every((w) => klein(e.titel).includes(w)) ? 0 : 1) + (e.erledigt ? 2 : 0);

  const treffer = passend
    .filter((e) => !arten || arten.has(e.art))
    .sort((x, y) => rang(x) - rang(y) || x.titel.localeCompare(y.titel, 'de'))
    .map((e) => {
      const text = a.woerter.length ? e.texte.find((t) => a.woerter.some((w) => klein(t).includes(w))) : undefined;
      return {
        eintrag: e,
        titel: markiere(e.titel, a.woerter),
        ausschnitt: text ? ausschnitt(text, a.woerter) : [],
      };
    });
  return { treffer, anzahl, woerter: a.woerter };
}

/** Wortlaut eigener Reflexionsfragen (F-27) - die Standardfragen stehen in jeder Reflexion und wuerden nur rauschen. */
const STANDARD_SCHLUESSEL = new Set([...WOCHEN_FRAGEN, ...MONATS_FRAGEN].map((f) => f.schluessel));
function eigeneFragen(wortlaut: Record<string, string> | undefined): string[] {
  return Object.entries(wortlaut ?? {}).filter(([k]) => !STANDARD_SCHLUESSEL.has(k)).map(([, w]) => w);
}
